/**
 * Debris Particle Effects
 * 
 * Configurable particle effect factories for debris impacts.
 * These create ParticlePreset configurations that can be customized per entity.
 */

import { Vector3, Color4, ParticleSystem } from '@babylonjs/core';
import type { ParticlePreset } from '../ParticleManager';

/**
 * Context provided to debris particle hooks
 */
export interface DebrisImpactContext {
  /** World position of the impact */
  position: Vector3;
  /** Debris body ID */
  debrisBodyId: string;
  /** Parent object ID (the object that spawned this debris) */
  parentObjectId: string | null;
  /** Debris piece index (0-7 typically) */
  debrisIndex: number;
}

/**
 * Result from a debris particle hook
 */
export interface DebrisParticleConfig {
  /** Particle preset name to use, or inline preset config */
  preset: string | ParticlePreset;
  /** Scale multiplier for the effect */
  scale?: number;
  /** Count multiplier for particles */
  countMultiplier?: number;
}

/**
 * Hook type for configuring debris particles
 */
export type DebrisParticleHook = (ctx: DebrisImpactContext) => DebrisParticleConfig | false | void;

// =============================================================================
// SMOKE / DUST EFFECTS
// =============================================================================

export interface SmokeOptions {
  /** Base color (default: brown/tan dust) */
  color?: { r: number; g: number; b: number };
  /** Opacity 0-1 (default: 0.5) */
  opacity?: number;
  /** Particle count (default: 25) */
  count?: number;
  /** Size range multiplier (default: 1) */
  sizeScale?: number;
  /** How fast particles rise (default: 1) */
  riseSpeed?: number;
  /** How long particles last in seconds (default: 0.7) */
  lifetime?: number;
  /** Spread radius (default: 0.2) */
  radius?: number;
}

/**
 * Create a configurable smoke/dust puff effect
 * 
 * @example
 * // Default dust
 * debrisParticle: () => smoke()
 * 
 * @example
 * // Light gray smoke
 * debrisParticle: () => smoke({ color: { r: 0.6, g: 0.6, b: 0.6 } })
 * 
 * @example
 * // Dense dark smoke
 * debrisParticle: () => smoke({ color: { r: 0.2, g: 0.2, b: 0.2 }, opacity: 0.8, count: 40 })
 */
export function smoke(options: SmokeOptions = {}): DebrisParticleConfig {
  const {
    color = { r: 0.7, g: 0.65, b: 0.6 },  // Default tan/dust color
    opacity = 0.5,
    count = 25,
    sizeScale = 1,
    riseSpeed = 1,
    lifetime = 0.7,
    radius = 0.2,
  } = options;

  const preset: ParticlePreset = {
    name: `smoke_${Date.now()}`,
    emitCount: count,
    lifetime: { min: lifetime * 0.6, max: lifetime * 1.4 },
    size: { min: 0.1 * sizeScale, max: 0.4 * sizeScale },
    velocity: { min: 0.5 * riseSpeed, max: 2 * riseSpeed },
    direction1: new Vector3(-1, 0.5, -1),
    direction2: new Vector3(1, 1.5, 1),
    color1: new Color4(color.r, color.g, color.b, opacity),
    color2: new Color4(color.r * 0.7, color.g * 0.7, color.b * 0.7, opacity * 0.6),
    colorDead: new Color4(color.r * 0.5, color.g * 0.5, color.b * 0.5, 0),
    blendMode: ParticleSystem.BLENDMODE_STANDARD,
    emitterType: 'sphere',
    emitterRadius: radius,
  };

  return { preset };
}

/**
 * Preset dust colors for common materials
 */
export const DustColors = {
  /** Brown/tan wood dust */
  wood: { r: 0.7, g: 0.55, b: 0.4 },
  /** Gray stone/concrete dust */
  stone: { r: 0.5, g: 0.5, b: 0.5 },
  /** Light gray dust */
  concrete: { r: 0.6, g: 0.6, b: 0.6 },
  /** Sandy tan */
  sand: { r: 0.85, g: 0.75, b: 0.55 },
  /** Dark gray/black soot */
  soot: { r: 0.2, g: 0.2, b: 0.2 },
  /** White chalk/plaster */
  plaster: { r: 0.9, g: 0.9, b: 0.85 },
  /** Reddish brick dust */
  brick: { r: 0.7, g: 0.4, b: 0.3 },
  /** Earthy brown dirt */
  dirt: { r: 0.5, g: 0.4, b: 0.3 },
} as const;

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/** Wood dust - warm brown */
export const woodDust = (opts?: Partial<SmokeOptions>) => 
  smoke({ color: DustColors.wood, ...opts });

/** Stone dust - neutral gray */
export const stoneDust = (opts?: Partial<SmokeOptions>) => 
  smoke({ color: DustColors.stone, ...opts });

/** Concrete dust - light gray */
export const concreteDust = (opts?: Partial<SmokeOptions>) => 
  smoke({ color: DustColors.concrete, ...opts });

/** No particles */
export const noParticles = (): false => false;

// =============================================================================
// FUTURE: ICE EFFECTS (placeholder)
// =============================================================================

// export interface IceOptions { ... }
// export function iceShatter(options: IceOptions = {}): DebrisParticleConfig { ... }
